/**
 * Media API – CRUD operations for media_assets + storage.
 *
 * Source of truth: media_assets.
 * Buckets:
 * - private-media: ONLY tmp/{userId}/{draftSessionId}/...
 * - public-media:  {ownerType}/{ownerId}/{kind}/...
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  MediaAsset,
  MediaBucket,
  MediaKind,
  OwnerType,
  VariantWidth,
} from './mediaTypes';
import type { ImageIntent } from './imageIntent';
import { intentPreferredVariant, VARIANT_FALLBACK_ORDER } from './imageIntent';

// ── Helpers ────────────────────────────────────────────

function getPublicUrl(bucket: MediaBucket, path: string): string {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Resolve a displayable URL for a media asset.
 * - public-media → public URL (instant)
 * - private-media → signed URL (temporary, for draft previews)
 *
 * For private-media the result is cached in a module-level Map so that
 * repeated calls during the same render don't trigger extra requests.
 */

const _signedCache = new Map<string, { url: string; expiresAt: number }>();

async function getSignedUrl(bucket: MediaBucket, path: string): Promise<string | null> {
  const cacheKey = `${bucket}:${path}`;
  const cached = _signedCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.url;

  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 600);
  if (error || !data?.signedUrl) return null;

  _signedCache.set(cacheKey, { url: data.signedUrl, expiresAt: Date.now() + 540_000 });
  return data.signedUrl;
}

/**
 * Synchronous URL resolver – works for public-media assets.
 * For private-media assets returns null (use resolveAssetUrlAsync instead).
 */
export function resolveAssetUrl(
  asset: MediaAsset,
  variant: VariantWidth = 320,
): string | null {
  // Private-media cannot be resolved synchronously
  if (asset.bucket === 'private-media') {
    // Check cache first
    const path = findBestPath(asset, variant);
    if (!path) return null;
    const cached = _signedCache.get(`private-media:${path}`);
    if (cached && cached.expiresAt > Date.now()) return cached.url;
    return null;
  }

  const path = findBestPath(asset, variant);
  if (!path) return null;
  return getPublicUrl(asset.bucket, path);
}

/**
 * Async URL resolver – works for both public and private buckets.
 * Use this in components that can handle async (useEffect/useQuery).
 */
export async function resolveAssetUrlAsync(
  asset: MediaAsset,
  variant: VariantWidth = 320,
): Promise<string | null> {
  const path = findBestPath(asset, variant);
  if (!path) return null;

  if (asset.bucket === 'private-media') {
    return getSignedUrl(asset.bucket, path);
  }
  return getPublicUrl(asset.bucket, path);
}

/**
 * Find the best available variant path for an asset.
 *
 * Uses VARIANT_FALLBACK_ORDER so that large-variant requests (hero/1280)
 * prefer to fall back to 640 before 320, while small-variant requests
 * (thumbnail/320) prefer to stay small before pulling larger files.
 */
function findBestPath(asset: MediaAsset, variant: VariantWidth): string | null {
  const order = VARIANT_FALLBACK_ORDER[variant];
  for (const v of order) {
    const path = asset[`path_${v}` as keyof MediaAsset] as string | null;
    if (path) return path;
  }
  return null;
}

/**
 * Resolve a displayable URL using an ImageIntent instead of a raw variant.
 * Convenience wrapper: intent → preferred variant → findBestPath → URL.
 *
 * IMPORTANT: Prefer this helper (or the async variant) in UI code.
 * This ensures intent-driven variant selection is consistently applied.
 */
export function resolveAssetUrlForIntent(
  asset: MediaAsset,
  intent: ImageIntent,
): string | null {
  return resolveAssetUrl(asset, intentPreferredVariant(intent));
}

/**
 * Async variant of resolveAssetUrlForIntent (supports private-media).
 */
export async function resolveAssetUrlForIntentAsync(
  asset: MediaAsset,
  intent: ImageIntent,
): Promise<string | null> {
  return resolveAssetUrlAsync(asset, intentPreferredVariant(intent));
}

/**
 * Internal guard to prevent mixed state rows.
 * - public-media => ownerId required, draftSessionId must be null
 * - private-media => draftSessionId required, ownerId must be null
 */
function assertBucketState(args: {
  bucket: MediaBucket;
  ownerId: string | null;
  draftSessionId: string | null;
}) {
  if (args.bucket === 'public-media') {
    if (!args.ownerId) throw new Error('public-media kräver ownerId.');
    if (args.draftSessionId) throw new Error('public-media får inte ha draftSessionId.');
  }
  if (args.bucket === 'private-media') {
    if (!args.draftSessionId) throw new Error('private-media kräver draftSessionId.');
    if (args.ownerId) throw new Error('private-media får inte ha ownerId.');
  }
}

// ── CRUD ───────────────────────────────────────────────

/**
 * Create a media_assets row for either draft or public mode.
 *
 * IMPORTANT:
 * - bucket + ownerId + draftSessionId must be consistent (validated).
 */
export async function createMediaAssetRow(params: {
  ownerType: OwnerType;
  ownerId: string | null;              // null in draft mode
  draftSessionId: string | null;       // null in public mode
  kind: MediaKind;
  bucket: MediaBucket;                // 'private-media' | 'public-media'
  mime: string;
  width: number;
  height: number;
  sizeBytes: number;
  altText?: string;
  sortOrder?: number;
  paths: Partial<Record<VariantWidth, string>>;
  seasonKey?: string | null;
}): Promise<MediaAsset> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;
  if (!userId) throw new Error('Inte inloggad');

  assertBucketState({
    bucket: params.bucket,
    ownerId: params.ownerId,
    draftSessionId: params.draftSessionId,
  });

  const row: Record<string, unknown> = {
    owner_type: params.ownerType,
    owner_id: params.ownerId,
    draft_session_id: params.draftSessionId,
    kind: params.kind,

    bucket: params.bucket,
    path_320: params.paths[320] ?? null,
    path_640: params.paths[640] ?? null,
    path_1280: params.paths[1280] ?? null,

    mime: params.mime,
    width: params.width,
    height: params.height,
    size_bytes: params.sizeBytes,
    alt_text: params.altText ?? null,
    sort_order: params.sortOrder ?? 0,

    created_by: userId,
  };

  if (params.seasonKey !== undefined) {
    row.season_key = params.seasonKey;
  }

  const { data, error } = await supabase
    .from('media_assets')
    .insert(row as any)
    .select('*')
    .single();

  if (error) throw error;
  return data as unknown as MediaAsset;
}

/**
 * Backwards-compatible wrapper (optional).
 * Keep this only if other code still calls createDraftAssetRow.
 *
 * Draft ALWAYS means private-media + ownerId null.
 */
export async function createDraftAssetRow(params: {
  ownerType: OwnerType;
  ownerId?: string | null;         // ignored (forced null)
  draftSessionId: string;
  kind: MediaKind;
  mime: string;
  width: number;
  height: number;
  sizeBytes: number;
  altText?: string;
  sortOrder?: number;
  paths: Record<VariantWidth, string>;
}): Promise<MediaAsset> {
  return createMediaAssetRow({
    ownerType: params.ownerType,
    ownerId: null,
    draftSessionId: params.draftSessionId,
    kind: params.kind,
    bucket: 'private-media',
    mime: params.mime,
    width: params.width,
    height: params.height,
    sizeBytes: params.sizeBytes,
    altText: params.altText,
    sortOrder: params.sortOrder,
    paths: params.paths,
  });
}

export async function listAssetsByOwner(
  ownerType: OwnerType,
  ownerId: string,
  kind?: MediaKind,
  /** Pass null to get only base (non-seasonal) assets, a season string to get that season, or undefined to get all */
  seasonKey?: string | null,
): Promise<MediaAsset[]> {
  let q = supabase
    .from('media_assets')
    .select('id, owner_type, owner_id, draft_session_id, kind, bucket, path_320, path_640, path_1280, mime, width, height, size_bytes, alt_text, sort_order, created_at, season_key')
    .eq('owner_type', ownerType)
    .eq('owner_id', ownerId)
    .order('sort_order', { ascending: true });

  if (kind) q = q.eq('kind', kind);

  // Filter by season_key: null = base only, string = specific season, undefined = all
  if (seasonKey === null) {
    q = q.is('season_key', null);
  } else if (seasonKey !== undefined) {
    q = q.eq('season_key', seasonKey as any);
  }

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as MediaAsset[];
}

export async function listAssetsByDraftSession(
  draftSessionId: string,
): Promise<MediaAsset[]> {
  const { data, error } = await supabase
    .from('media_assets')
    .select('*')
    .eq('draft_session_id', draftSessionId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data ?? []) as unknown as MediaAsset[];
}

export async function deleteAsset(assetId: string): Promise<void> {
  const { data: asset, error: fetchErr } = await supabase
    .from('media_assets')
    .select('*')
    .eq('id', assetId)
    .single();

  if (fetchErr) throw fetchErr;
  if (!asset) return;

  const a = asset as unknown as MediaAsset;

  const pathsToDelete = [a.path_320, a.path_640, a.path_1280].filter(Boolean) as string[];
  if (pathsToDelete.length > 0) {
    await supabase.storage.from(a.bucket).remove(pathsToDelete);
  }

  // Use count:'exact' so RLS silent-block (0 rows affected, no error) is surfaced.
  const { error, count } = await supabase
    .from('media_assets')
    .delete({ count: 'exact' })
    .eq('id', assetId);
  if (error) throw error;
  if (count === 0) throw new Error('Bilden hittades inte eller saknas behörighet för att ta bort');
}

export async function updateAssetSortOrder(
  assetId: string,
  sortOrder: number,
): Promise<void> {
  const { error } = await supabase
    .from('media_assets')
    .update({ sort_order: sortOrder } as any)
    .eq('id', assetId);
  if (error) throw error;
}

/**
 * Finalize a draft session by calling the server-side Edge Function.
 */
export async function finalizeDraftSession(
  draftSessionId: string,
  ownerType: OwnerType,
  ownerId: string,
): Promise<{ ok: boolean; moved: number }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error('Inte inloggad');

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const res = await fetch(`${supabaseUrl}/functions/v1/finalize-media-draft`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ draftSessionId, ownerType, ownerId }),
  });

  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.error ?? 'Finalisering misslyckades');
  }
  return json;
}

/**
 * Upload a single variant blob to storage.
 */
export async function uploadVariantBlob(
  bucket: MediaBucket,
  path: string,
  blob: Blob,
): Promise<void> {
  const { error } = await supabase.storage.from(bucket).upload(path, blob, {
    contentType: 'image/webp',
    upsert: false, // IMPORTANT: avoid overwriting silently
    cacheControl: '3600',
  });
  if (error) throw error;
}

/**
 * Set an existing asset as cover (atomic swap via RPC).
 * The previous cover is demoted to gallery. Total count unchanged.
 */
export async function setCoverAsset(assetId: string): Promise<void> {
  const { error } = await supabase.rpc('set_media_cover', {
    p_asset_id: assetId,
  });
  if (error) throw error;
}

/**
 * Reorder gallery assets. Saves sort_order = array index on server.
 * Only affects kind='gallery' assets for the given owner.
 */
export async function reorderGalleryAssets(
  ownerType: OwnerType,
  ownerId: string,
  assetIds: string[],
): Promise<void> {
  const { error } = await supabase.rpc('reorder_media_gallery', {
    p_owner_type: ownerType,
    p_owner_id: ownerId,
    p_asset_ids: assetIds,
  });
  if (error) throw error;
}
